import BaseModal, { ModalProps } from './base-modal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Props as ButtonProps } from './button';
import { CloseButton } from './close-button';
import { createStyles } from '@/contexts/theme-context';
import { HeaderBackButton } from './header-back-button';
import { KeyboardScope } from '@/platform/shortcuts/operator-shortcuts';
import {
  Platform,
  ScrollView,
  StyleProp,
  Text,
  useWindowDimensions,
  View,
  ViewStyle,
} from 'react-native';
import { shadow, TYPOGRAPHY } from '@/styles';
import { useKeyboardHotkeysContext } from '@/contexts/keyboard-shortcuts/keyboard-shortcuts-context';
import { useLayoutContext } from '@/contexts/layout-context';

const DEFAULT_BORDER_RADIUS = 16;

export enum RenderType {
  DEFAULT = 'DEFAULT',
  POPOVER = 'POPOVER',
}

interface PopoverCoordinates {
  height: number;
  width: number;
  x: number;
  y: number;
}

type PopoverStyleFn = (
  contentLocation: PopoverCoordinates,
  targetLocation: PopoverCoordinates,
) => StyleProp<ViewStyle>;

type StyleFn =
  | StyleProp<ViewStyle>
  | ((renderType: RenderType) => StyleProp<ViewStyle>);

export type Props = Omit<Partial<ModalProps>, 'style'> & {
  additionalAction?: React.ReactNode;
  contentContainerStyle?: StyleFn;
  displayCloseButtonOverride?: boolean;
  hasStickyHeader?: boolean;
  headerContent?: React.ReactNode;
  isFocusableForKeyboardShortcuts?: boolean;
  modalCloseButtonProps?: Partial<ButtonProps>;
  modalHeaderStyle?: StyleFn;
  modalStyle?: StyleFn;
  onModalBackArrowPress?: () => void;
  onModalCloseButtonPress?: () => void;
  popoverStyle?: PopoverStyleFn;
  popoverTarget?: View | null;
  showSmallLayoutHeader?: boolean;
  style?: StyleFn;
  title?: React.ReactNode;
};

export const Modal: React.FC<Props> = ({
  additionalAction,
  children,
  contentContainerStyle,
  displayCloseButtonOverride = false,
  hasStickyHeader,
  headerContent,
  isFocusableForKeyboardShortcuts = false,
  modalCloseButtonProps,
  modalHeaderStyle,
  modalStyle,
  onModalBackArrowPress,
  onModalCloseButtonPress,
  popoverTarget,
  popoverStyle,
  showSmallLayoutHeader,
  style,
  title,
  ...rest
}) => {
  const { appDimensions, isSmallLayout } = useLayoutContext();
  const { width, height } = useWindowDimensions();
  const { disableScope, enableScope } = useKeyboardHotkeysContext();
  const [styles, theme] = useStyles();

  const contentRef = useRef<View>(null);
  const [popoverContentContainerStyle, setPopoverContentContainerStyle] =
    useState<StyleProp<ViewStyle> | null>(null);

  useEffect(() => {
    if (
      Platform.OS !== 'web' ||
      !rest.isVisible ||
      !isFocusableForKeyboardShortcuts
    ) {
      return;
    }

    if (rest.isVisible && contentRef.current) {
      contentRef.current.focus();
      disableScope(KeyboardScope.ORDER_SUMMARY);
      enableScope(KeyboardScope.ACTION_SHEET);
    }

    return () => {
      disableScope(KeyboardScope.ACTION_SHEET);
      enableScope(KeyboardScope.ORDER_SUMMARY);
    };
  }, [
    disableScope,
    enableScope,
    isFocusableForKeyboardShortcuts,
    rest.isVisible,
  ]);

  const renderAsPopover = Boolean(!isSmallLayout && popoverTarget);

  const getStyle = useCallback(
    (styleFn: StyleFn) =>
      typeof styleFn === 'function'
        ? styleFn(renderAsPopover ? RenderType.POPOVER : RenderType.DEFAULT)
        : styleFn,
    [renderAsPopover],
  );

  if (renderAsPopover) {
    return (
      <BaseModal
        {...rest}
        // A short animation duration can cause bugs like breaking auto-scroll,
        // so this disables the animation without changing the duration
        animationIn={{
          from: {
            opacity: 1,
          },
          to: {
            opacity: 1,
          },
        }}
        animationOut={{
          from: {
            opacity: 0,
          },
          to: {
            opacity: 0,
          },
        }}
        backdropColor={theme.colors.transparent}
        onDismiss={() => setPopoverContentContainerStyle(null)}
        style={[getStyle(style), styles.popover]}
        testID="popover"
      >
        <View
          onLayout={() => {
            if (
              popoverTarget &&
              rest.isVisible &&
              !popoverContentContainerStyle
            ) {
              popoverTarget.measureInWindow(
                (targetX, targetY, targetWidth, targetHeight) => {
                  contentRef.current?.measureInWindow(
                    (contentX, contentY, contentWidth, contentHeight) => {
                      const positionStyle = popoverStyle
                        ? popoverStyle(
                            {
                              height: contentHeight,
                              width: contentWidth,
                              x: contentX,
                              y: contentY,
                            },
                            {
                              height: targetHeight,
                              width: targetWidth,
                              x: targetX,
                              y: targetY,
                            },
                          )
                        : {
                            left:
                              // Anchor on the left side of the target if the popover
                              // content width does not exceed the window width,
                              // otherwise anchor on the right
                              targetX + contentWidth > appDimensions.width
                                ? targetX - (contentWidth - targetWidth)
                                : targetX,
                            top:
                              // If the popover content height exceeds the window height,
                              // anchor it above the target
                              targetY + contentHeight > appDimensions.height
                                ? targetY - contentHeight > 0
                                  ? targetY - contentHeight
                                  : targetY + targetHeight
                                : targetY + targetHeight,
                          };
                      setPopoverContentContainerStyle([
                        styles.popoverContentContainerVisible,
                        positionStyle,
                      ]);
                    },
                  );
                },
              );
            }
          }}
          ref={contentRef}
          style={[
            getStyle(contentContainerStyle),
            styles.popoverContentContainer,
            popoverContentContainerStyle,
          ]}
        >
          {children}
        </View>
      </BaseModal>
    );
  }

  const modalContent =
    !isSmallLayout && hasStickyHeader ? (
      <ScrollView contentContainerStyle={getStyle(contentContainerStyle)}>
        {children}
      </ScrollView>
    ) : (
      <View
        ref={contentRef}
        style={getStyle(contentContainerStyle)}
        tabIndex={-1}
      >
        {(isSmallLayout && showSmallLayoutHeader) ||
        displayCloseButtonOverride ? (
          <View style={[styles.modalHeaderSmall, getStyle(modalHeaderStyle)]}>
            {onModalCloseButtonPress ? (
              <CloseButton
                onPress={onModalCloseButtonPress}
                testID="modal-close-button"
                trackingData={{ name: 'modal.close_button' }}
                {...modalCloseButtonProps}
              />
            ) : null}
            {title ? (
              <Text
                numberOfLines={1}
                style={[TYPOGRAPHY.h3Bold, styles.titleSmall]}
              >
                {title}
              </Text>
            ) : null}
          </View>
        ) : null}
        {isSmallLayout && headerContent ? headerContent : null}
        {children}
      </View>
    );

  // @ts-ignore deviceHeight / deviceWidth types are null in react-native-modal
  const backdropDimensions = {
    deviceHeight: height,
    deviceWidth: width,
  } as Partial<ModalProps>;

  return isSmallLayout ? (
    <BaseModal {...rest} {...backdropDimensions} style={getStyle(style)}>
      {modalContent}
    </BaseModal>
  ) : (
    <BaseModal
      {...rest}
      {...backdropDimensions}
      animationIn="fadeIn"
      animationInTiming={1}
      animationOut="fadeOut"
      animationOutTiming={1}
      backdropTransitionInTiming={1}
      backdropTransitionOutTiming={1}
      style={[styles.baseLarge, getStyle(style)]}
    >
      <View style={[styles.modal, getStyle(modalStyle)]}>
        <View style={[styles.modalHeader, getStyle(modalHeaderStyle)]}>
          {headerContent ? (
            headerContent
          ) : (
            <>
              <View style={styles.titleLeft}>
                {onModalBackArrowPress ? (
                  <HeaderBackButton
                    containerStyle={styles.backButton}
                    iconProps={{
                      testID: 'modal-back-button',
                    }}
                    onPress={onModalBackArrowPress}
                  />
                ) : null}
              </View>
              <View style={styles.titleCenter}>
                {title ? (
                  <Text numberOfLines={1} style={TYPOGRAPHY.h3Bold}>
                    {title}
                  </Text>
                ) : null}
              </View>
            </>
          )}
          <View style={styles.titleRight}>
            {additionalAction}
            {onModalCloseButtonPress ? (
              <CloseButton
                onPress={onModalCloseButtonPress}
                testID="modal-close-button"
                trackingData={{ name: 'modal.close_button' }}
                {...modalCloseButtonProps}
              />
            ) : null}
          </View>
        </View>
        {modalContent}
      </View>
    </BaseModal>
  );
};

const POPOVER_SHADOW = shadow({
  elevation: 5,
  opacity: 0.15,
  radius: 12,
  x: 0,
  y: 0,
});

const useStyles = createStyles((theme) => ({
  backButton: {
    margin: 0,
    paddingVertical: 0,
  },
  baseLarge: {
    margin: 0,
  },
  modal: {
    alignSelf: 'center',
    backgroundColor: theme.colors.white,
    borderRadius: DEFAULT_BORDER_RADIUS,
    flexShrink: 1,
    marginVertical: 40,
    maxWidth: 640,
    overflow: 'hidden',
    width: '100%',
    ...(Platform.OS === 'web' ? { overflowY: 'auto' } : {}),
  },
  modalHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    padding: 16,
  },
  modalHeaderSmall: {
    alignItems: 'center',
    flexDirection: 'row',
    padding: 16,
  },
  // overflow: hidden on popoverContentContainer clips the shadow on native
  // so it is conditionally set here
  popover: Platform.OS === 'web' ? {} : POPOVER_SHADOW,
  popoverContentContainer: {
    backgroundColor: theme.colors.white,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minWidth: 240,
    opacity: 0,
    overflow: 'hidden',
    position: 'absolute',
    ...(Platform.OS === 'web' ? POPOVER_SHADOW : {}),
  },
  popoverContentContainerVisible: {
    opacity: 1,
  },
  titleCenter: {
    alignItems: 'center',
    flex: 5,
    justifyContent: 'center',
  },
  titleLeft: {
    flex: 1,
    marginLeft: Platform.OS === 'web' ? 10 : -16,
    marginTop: -4,
  },
  titleRight: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  titleSmall: {
    marginHorizontal: 12,
  },
}));
